import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  ENTITY_CATEGORY_FILTER_VALUES,
  MATERIAL_STATUSES,
  MATERIAL_TYPE,
  MILLIS,
} from '@repo/constants'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const materialsSearchSchema = z.object({
  keyword: z.string().optional(),
  priced: z.boolean().optional(),
  status: z.array(z.enum(MATERIAL_STATUSES)).optional(),
  type: z.array(z.enum(MATERIAL_TYPE)).optional(),
  // Nova only. Declared here rather than left undeclared, because an undeclared search param is
  // silently stripped and the filter would answer with an unfiltered list.
  entityCategory: z.array(z.enum(ENTITY_CATEGORY_FILTER_VALUES)).optional(),
  ...datatableSearchSchema.shape,
})

export type MaterialsSearch = z.infer<typeof materialsSearchSchema>

export async function getMaterials(search: MaterialsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.materials.$get({ query: stringifyValues(search) }, { init: { signal } })

  const materials = await res.json()
  return materials
}

export async function getMaterialFilterValues(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.materials.options.$get(
    { query: { onlyActive: 'false' } },
    { init: { signal } },
  )

  const materials = (await res.json()).map((m) => ({
    value: m.value ?? '',
    label: m.label,
    keywords: m.keywords,
  }))

  return materials.sort((a, b) => a.label.localeCompare(b.label))
}

export async function getMaterialsOptions(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.materials.options.$get(
    { query: { onlyActive: 'true' } },
    { init: { signal } },
  )

  return (await res.json()).map((m) => ({
    value: m.value ?? '',
    label: m.label,
    keywords: m.keywords,
  }))
}

export async function getMaterialDetails(material: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.materials[':material'].$get({ param: { material } }, { init: { signal } })

  const materialDetails = await res.json()
  return materialDetails
}

export function useMaterials(queries: MaterialsSearch) {
  return useQuery({
    queryKey: ['materials', queries],
    queryFn: ({ signal }) => getMaterials(queries, signal),
  })
}

export function useMaterialFilter() {
  return useQuery({
    queryKey: ['materials', 'unique'],
    queryFn: ({ signal }) => getMaterialFilterValues(signal),
  })
}

export function useMaterialsOptions() {
  return useQuery({
    queryKey: ['materialsOptions'],
    queryFn: ({ signal }) => getMaterialsOptions(signal),
    staleTime: MILLIS.MINUTE * 5,
  })
}

export const materialDetailsQueryOptions = (material: string) => ({
  queryKey: ['materialDetails', material],
  queryFn: ({ signal }: { signal: AbortSignal }) => getMaterialDetails(material, signal),
})
